import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { Loader2 } from "lucide-react";
import type { RoleSchema } from "./roleProfileSchema";

export function ProfileVisibility({
  schema,
  initial,
  onSave,
  saving,
}: {
  schema: RoleSchema;
  initial?: Record<string, boolean>;
  onSave: (visibility: Record<string, boolean>) => Promise<void>;
  saving: boolean;
}) {
  const { language } = useLanguage();
  const lang = (language as "en" | "fr") || "en";
  const [vis, setVis] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const next: Record<string, boolean> = {};
    schema.sections.forEach((s) =>
      s.fields.forEach((f) => {
        next[f.key] = initial?.[f.key] ?? !!f.publicDefault;
      })
    );
    setVis(next);
  }, [schema, initial]);

  const { toast } = useToast();
  const t = lang === "fr"
    ? { title: "Visibilité publique", subtitle: "Choisissez les champs visibles par les vendeurs et brokers.", save: "Enregistrer", saved: "Visibilité enregistrée", public: "Public", private: "Privé", documents: "Documents", cv: "CV", competencyCard: "Carte de compétence", awards: "Prix / Distinctions", otherDoc: "Autres documents" }
    : { title: "Public visibility", subtitle: "Choose which fields are visible to sellers and brokers.", save: "Save", saved: "Visibility saved", public: "Public", private: "Private", documents: "Documents", cv: "CV / Resume", competencyCard: "Competency Card", awards: "Awards / Honors", otherDoc: "Other documents" };

  const documentKeys: Array<{ key: string; label: string }> = [
    { key: "documents.cv", label: t.cv },
    { key: "documents.competencyCard", label: t.competencyCard },
    { key: "documents.awards", label: t.awards },
    { key: "documents.other", label: t.otherDoc },
  ];

  const handleSave = async () => {
    await onSave(vis);
    // Notify profile editor to re-sync publicProfiles
    window.dispatchEvent(new CustomEvent("profile:visibility-changed"));
    toast({ title: t.saved });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.title}</CardTitle>
        <p className="text-sm text-muted-foreground">{t.subtitle}</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {schema.sections.map((s) => (
          <div key={s.id} className="space-y-2">
            <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">{s.title[lang]}</h3>
            <div className="rounded-md border divide-y">
              {s.fields.map((f) => (
                <div key={f.key} className="flex items-center justify-between p-3">
                  <span className="text-sm">{f.label[lang]}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{vis[f.key] ? t.public : t.private}</span>
                    <Switch
                      checked={!!vis[f.key]}
                      onCheckedChange={(v) => setVis((p) => ({ ...p, [f.key]: v }))}
                      disabled={f.readonly}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="space-y-2">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">{t.documents}</h3>
          <div className="rounded-md border divide-y">
            {documentKeys.map((d) => (
              <div key={d.key} className="flex items-center justify-between p-3">
                <span className="text-sm">{d.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{vis[d.key] ? t.public : t.private}</span>
                  <Switch
                    checked={!!vis[d.key]}
                    onCheckedChange={(v) => setVis((p) => ({ ...p, [d.key]: v }))}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t.save}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
